// src/context/AgreementContext.js

import React, {
  createContext,
  useContext,
  useState,
  useCallback
} from "react";
import axiosInstance from "../AxiosInstance";
import config from "../config/config";

const AgreementContext = createContext();

export function AgreementProvider({ children }) {
  const [agreement, setAgreement] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // LIST ALL: GET /v1/agreements/all
  const listAllAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/agreements/all`
      );
      setAgreements(response.data);
      return response.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // CREATE: POST /v1/agreements
  const createAgreement = useCallback(async (payload) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${config.API_BASE_URL}/v1/agreements`,
        payload
      );
      setAgreement(response.data);
      return response.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // GET BY ID: GET /v1/agreements/{agreementId}
  const getAgreementById = useCallback(async (agreementId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/agreements/${agreementId}`
      );
      setAgreement(response.data);
      return response.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // LIST BY USER: GET /v1/agreements/user/{userType}/{userId}
  const listAgreementsByUser = useCallback(async (userType, userId) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `${config.API_BASE_URL}/v1/agreements/user/${userType}/${userId}`
      );
      setAgreements(data);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // GET BY USER & AGREEMENT: GET /v1/agreements/user/{userType}/{userId}/agreement/{agreementId}
  const getByUserAndAgreement = useCallback(
    async (userType, userId, agreementId) => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${config.API_BASE_URL}/v1/agreements/user/${userType}/${userId}/agreement/${agreementId}`
        );
        setAgreement(response.data);
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

// GET BY CLIENT, WORKER & JOB: GET /v1/agreements/user/{userType}/{userId}/job/{jobId}/worker/{workerId}
  const getAgreementByClientWorkerJob = useCallback(
    async (userType, clientId, jobId, workerId) => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${config.API_BASE_URL}/v1/agreements/user/${userType}/${clientId}/job/${jobId}/worker/${workerId}`
        );
        setAgreement(response.data);
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // SIGN: PATCH /v1/agreements/sign/{userType}/{agreementId}
  const signAgreement = useCallback(
    async (userType, agreementId) => {
      setLoading(true);
      try {
        const response = await axiosInstance.patch(
          `${config.API_BASE_URL}/v1/agreements/sign/${userType}/${agreementId}`
        );
        setAgreement(response.data);
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // UPDATE: PUT /v1/agreements/{agreementId}
  const updateAgreement = useCallback(
    async (agreementId, payload) => {
      setLoading(true);
      try {
        const response = await axiosInstance.put(
          `${config.API_BASE_URL}/v1/agreements/${agreementId}`,
          payload
        );
        setAgreement(response.data);
        return response.data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // DELETE: DELETE /v1/agreements/{agreementId}
  const deleteAgreement = useCallback(
    async (agreementId) => {
      setLoading(true);
      try {
        await axiosInstance.delete(
          `${config.API_BASE_URL}/v1/agreements/${agreementId}`
        );
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // MARK VIEWED: PATCH /v1/agreements/{agreementId}/viewed
  const markAgreementViewed = useCallback(
  async (userType, userId, agreementId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.patch(
        `${config.API_BASE_URL}/v1/agreements/user/${userType}/${userId}/agreement/${agreementId}/viewed`
      );
      setAgreement(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  },
  []
);

  return (
    <AgreementContext.Provider
      value={{
        agreement,
        agreements,
        loading,
        error,
        listAllAgreements,
        createAgreement,
        getAgreementById,
        listAgreementsByUser,
        getByUserAndAgreement,
        getAgreementByClientWorkerJob,
        signAgreement,
        updateAgreement,
        deleteAgreement,
        markAgreementViewed,
      }}
    >
      {children}
    </AgreementContext.Provider>
  );
}

export function useAgreement() {
  return useContext(AgreementContext);
}
